/**
 * Umami Analytics Tracking Utilities
 * 
 * Simple tracking for municipality token-based pages
 */

import { detectTestMode } from './test-mode';

// Extend window interface for tracking timestamps
declare global {
  interface Window {
    __landingPageLoadTime?: number;
  }
}

export interface UmamiEventData {
  gemeinde_id?: string;
  gemeinde_name?: string;
  type?: 'telefon' | 'rueckruf' | 'webinar';
  // Landing page specific
  position?: 'hero' | 'footer';
  referrer?: string;
  viewport_width?: number;
  device_type?: 'mobile' | 'tablet' | 'desktop';
  // Scroll tracking
  milestone?: 25 | 50 | 75 | 100;
  time_to_milestone_ms?: number;
  section_visible?: string;
  // Modal/Form tracking
  trigger?: string;
  time_on_page_ms?: number;
  reason?: string;
  form_progress?: string;
  step?: 'basic_info' | 'optional_info';
  first_field?: string;
  rolle?: string;
  [key: string]: any;
}

/**
 * Track an event in Umami Analytics
 */
export const trackUmamiEvent = (
  eventName: string, 
  data?: UmamiEventData
) => {
  // Skip tracking in test mode
  if (detectTestMode()) {
    // Silent skip in test mode
    return;
  }
  
  if (typeof window !== 'undefined' && window.umami) {
    try {
      window.umami.track(eventName, data);
      // Silent tracking - no console logs
    } catch (error) {
      // Only log errors, not successful tracking
      console.error('[Umami] Error tracking event:', error);
    }
  }
};

/**
 * Track municipality page view
 */
export const trackGemeindeView = (gemeindeId: string, gemeindeName: string) => {
  trackUmamiEvent('gemeinde_angebot_view', {
    gemeinde_id: gemeindeId,
    gemeinde_name: gemeindeName
  });
};

/**
 * Track PDF download
 */
export const trackPDFDownload = (gemeindeId: string, gemeindeName: string) => {
  trackUmamiEvent('pdf_download', {
    gemeinde_id: gemeindeId,
    gemeinde_name: gemeindeName
  });
};

/**
 * Track conversion (contact action)
 */
export const trackConversion = (
  type: 'telefon' | 'rueckruf' | 'webinar' | 'webinar_expand' | 'webinar_collapse',
  gemeindeId: string,
  gemeindeName: string,
  additionalData?: any
) => {
  trackUmamiEvent('conversion', {
    type,
    gemeinde_id: gemeindeId,
    gemeinde_name: gemeindeName,
    ...additionalData
  });
};

/**
 * Landing Page Events
 */

/**
 * Track landing page view
 */
export const trackLandingPageView = () => {
  const getDeviceType = (): 'mobile' | 'tablet' | 'desktop' => {
    const width = window.innerWidth;
    if (width < 768) return 'mobile';
    if (width < 1024) return 'tablet';
    return 'desktop';
  };

  trackUmamiEvent('landing_page_view', {
    referrer: document.referrer || 'direct',
    viewport_width: window.innerWidth,
    device_type: getDeviceType()
  });
};

/**
 * Track CTA button click
 */
export const trackCTAClick = (position: 'hero' | 'footer') => {
  trackUmamiEvent('cta_click', {
    position,
    button_text: 'Jetzt starten',
    timestamp: new Date().toISOString()
  });
};

/**
 * Track scroll milestone
 */
export const trackScrollMilestone = (
  milestone: 25 | 50 | 75 | 100,
  sectionVisible?: string
) => {
  const startTime = window.__landingPageLoadTime || Date.now();
  const timeElapsed = Date.now() - startTime;

  trackUmamiEvent(`scroll_${milestone}`, {
    milestone,
    time_to_milestone_ms: timeElapsed,
    section_visible: sectionVisible
  });
};

/**
 * Modal & Form Events
 */

/**
 * Track modal events
 */
export const trackModalEvent = (
  action: 'open' | 'close',
  data?: {
    trigger?: string;
    reason?: string;
    form_progress?: string;
  }
) => {
  const startTime = window.__landingPageLoadTime || Date.now();
  const timeOnPage = Date.now() - startTime;

  trackUmamiEvent(`modal_${action}`, {
    time_on_page_ms: timeOnPage,
    ...data
  });
};

/**
 * Track form events
 */
export const trackFormEvent = (
  action: 'start' | 'submit' | 'complete' | 'abandon',
  data?: {
    first_field?: string;
    step?: 'basic_info' | 'optional_info';
    gemeinde_id?: string;
    rolle?: string;
    last_field_completed?: string;
    fields_completed_count?: number;
    time_in_form_ms?: number;
  }
) => {
  trackUmamiEvent(`form_${action}`, data);
};

// Store page load time for time-based metrics
if (typeof window !== 'undefined') {
  window.__landingPageLoadTime = Date.now();
}